// Per-project saved-views cache. Mirrors the pattern of store/statuses.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  listViews,
  createView,
  updateView,
  deleteView,
  type SavedView,
  type SavedViewCreate,
  type SavedViewUpdate,
} from "../lib/api";

export type { SavedView };

export interface ViewsState {
  loading: boolean;
  items: SavedView[];
  projectId: number | null;
  error: string | null;
}

const initial: ViewsState = {
  loading: false,
  items: [],
  projectId: null,
  error: null,
};

const viewsSignal: Signal<ViewsState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadViews(projectId: number): Promise<void> {
  try {
    const items = await listViews(projectId);
    viewsSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    viewsSignal.value = { loading: false, items: [], projectId, error: message };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to saved views for a project.
 * Returns stale-while-loading guard: never exposes previous project's items.
 */
export function useViews(projectId: number): ViewsState {
  useEffect(() => {
    if (!inFlight && viewsSignal.value.projectId !== projectId) {
      viewsSignal.value = { loading: true, items: [], projectId, error: null };
      inFlight = loadViews(projectId);
    }
  }, [projectId]);

  const state = viewsSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

function refreshViews(projectId: number): Promise<void> {
  viewsSignal.value = { ...viewsSignal.value, loading: true };
  inFlight = loadViews(projectId);
  return inFlight;
}

/** Create a new saved view and refresh the cache. */
export async function addView(
  projectId: number,
  body: SavedViewCreate
): Promise<SavedView> {
  const created = await createView(projectId, body);
  await refreshViews(projectId);
  return created;
}

/** Update a saved view (name / visibility / spec) and refresh. */
export async function patchView(
  id: number,
  projectId: number,
  body: SavedViewUpdate
): Promise<SavedView> {
  const updated = await updateView(id, body);
  await refreshViews(projectId);
  return updated;
}

/** Delete a saved view and remove from cache optimistically. */
export async function removeView(id: number, projectId: number): Promise<void> {
  // Optimistic remove
  const cur = viewsSignal.value;
  if (cur.projectId === projectId) {
    viewsSignal.value = { ...cur, items: cur.items.filter((v) => v.id !== id) };
  }
  try {
    await deleteView(id);
  } catch (e) {
    // Revert on error
    await refreshViews(projectId);
    throw e;
  }
}
